import produce from 'immer';

import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ProcessFlowchartInteractiveNode,
  ProcessInteractiveFlowchart,
} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {ProcessFlowchartObjectType} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {
  ProcessEndNodeForValidation,
  BaseNodeForValidation,
  ProcessRouterNodeForValidation,
  ProcessNodeForValidation,
  ProcessTaskNodeForValidation,
  ProcessFlowchartForValidation,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {ProcessFlowchartDesignSpace} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import validateProcessFlowchart from 'processes/graph-builder-processes/validation/validateProcessFlowchart';
import {ProcessFlowchartEdgeType} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

function translateNode(node: ProcessFlowchartInteractiveNode): ProcessNodeForValidation {
  const baseNode: BaseNodeForValidation = {
    id: node.clientId,
    name: node.name,
    nodeType: node.type,
  };
  if (node.type === ProcessFlowchartNodeDetailsType.END_NODE_DETAILS) {
    return baseNode as ProcessEndNodeForValidation;
  } else if (node.type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
    return {...baseNode, routerConfig: node.routerConfig} as ProcessRouterNodeForValidation;
  } else {
    return {...baseNode, taskSchemaId: node.taskSchemaId} as ProcessTaskNodeForValidation;
  }
}

export default function validateProcessInteractiveFlowchart(
  flowchart: ProcessInteractiveFlowchart,
  designSpace: ProcessFlowchartDesignSpace,
  didInitialFlowchartHaveChainedRouters: boolean
): ProcessInteractiveFlowchart {
  const flowchartForValidation: ProcessFlowchartForValidation = {
    id: flowchart.clientId,
    nodes: flowchart.nodes.map((node) => translateNode(node)),
    edges: flowchart.edges.map((edge) => ({
      id: edge.clientId,
      source: edge.fromNodeClientId,
      target: edge.toNodeClientId,
      type: ProcessFlowchartEdgeType.CONTROL_FLOW,
    })),
  };

  const validationResult = validateProcessFlowchart({
    flowchart: flowchartForValidation,
    designSpace,
    didInitialFlowchartHaveChainedRouters,
    usingFlowchartFirstConfig: false,
  });

  return produce(flowchart, (draftFlowchart) => {
    draftFlowchart.nodes = draftFlowchart.nodes.map((node) => ({
      ...node,
      errors: [],
      errorTypes: [],
    }));
    draftFlowchart.edges = draftFlowchart.edges.map((edge) => ({...edge, errors: [], errorTypes: []}));
    for (const error of validationResult.errors) {
      if (error.object?.type === ProcessFlowchartObjectType.NODE) {
        const node = draftFlowchart.nodes.find(({clientId}) => clientId === error.object?.id);
        if (node != null) {
          node.errors = [...(node.errors ?? []), error.errorMessage ?? ''];
          node.errorTypes = [...(node.errorTypes ?? []), error.errorType];
        }
      } else if (error.object?.type === ProcessFlowchartObjectType.EDGE) {
        const edge = draftFlowchart.edges.find(({clientId}) => clientId === error.object?.id);
        if (edge != null) {
          edge.errors = [...(edge.errors ?? []), error.errorMessage ?? ''];
          edge.errorTypes = [...(edge.errorTypes ?? []), error.errorType];
        }
      } else if (error.object != null) {
        assert(false, 'Unexpected flowchart validation error', {error});
      }
    }
  });
}
